import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { BrowserRouter as Router, NavLink, useLocation } from 'react-router-dom';
import './App.css';
import { styled } from '@mui/material/styles';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import VersionBadge from './components/VersionBadge';
import AppRoutes from './app/AppRoutes';
import { getAuthProfile } from './services/api';
import {
  AUTH_PROFILE_STORAGE_KEY,
  logoutAndRedirect,
  readStoredAuthProfile,
} from './utils/authSession';
import {
  RECENT_MENU_EVENT,
  getRecentMenuItems,
  readRecentMenuRoutes,
} from './utils/recentMenu';

const PUBLIC_ROUTE_PATHS = new Set(['/', '/login', '/register', '/forgot-password', '/reset-password', '/pmi/registration', '/privacy-policy', '/terms-of-use', '/account-deletion']);
const PUBLIC_ROUTE_PREFIXES = ['/privacy-consent/', '/campaign-access/'];
const AUTH_ROUTE_PATHS = new Set(['/login', '/register', '/forgot-password', '/reset-password']);
const WALL_ROUTE_PATHS = new Set(['/proof-of-play-live', '/dashboard-sofia-edition-live']);
const V2_SHELL_ROUTE_PATHS = new Set(['/pmi-devices-v2', '/pmi-locations-v2', '/list-files-v2', '/campaign-calendar-v2', '/mail-tracking-v2', '/device-notifications-v2', '/change-log', '/simulation-results', '/location-health-v2', '/access-audit-logs-v2', '/notify-flow-v2', '/audit-v2', '/roles-permissions-v2', '/qa-cycle-v2', '/device-watch-v2', '/qa-simulations-v2', '/device-observation-v2', '/device-configs', '/device-configs-v2', '/feedback-board']);
const drawerWidth = 200;
const CHUNK_RELOAD_GUARD_KEY = 'onlabs:chunk-reload-guard';

function getCurrentMainBundleKey() {
  if (typeof document === 'undefined') return 'unknown-main-bundle';
  return document
    .querySelector('script[src*="/static/js/main."]')
    ?.getAttribute('src')
    || 'unknown-main-bundle';
}

function isChunkLoadError(candidate) {
  const message = String(
    candidate?.message
    || candidate?.reason?.message
    || candidate?.target?.src
    || ''
  );

  return (
    message.includes('ChunkLoadError')
    || message.includes('Loading chunk')
    || message.includes('Failed to fetch dynamically imported module')
  );
}
const Main = styled('main', {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'shellVisible' && prop !== 'isMobile',
})(({ theme, open, shellVisible, isMobile }) => ({
	    flexGrow: 1,
	    minWidth: 0,
	    padding: shellVisible ? theme.spacing(2) : 0,
	    paddingTop: shellVisible ? `calc(var(--platform-header-height, 56px) + ${theme.spacing(2)})` : 0,
	    transition: theme.transitions.create('margin', {
	      easing: theme.transitions.easing.sharp,
	      duration: theme.transitions.duration.leavingScreen,
	    }),
	    marginLeft: shellVisible && !isMobile && !open ? `-${drawerWidth}px` : 0,
	  }));

function AppLayout({ token, open, setOpen, isMobile, menuAccess, authProfile, saveToken }) {
  const location = useLocation();
  const isPublicRoute = PUBLIC_ROUTE_PATHS.has(location.pathname)
    || PUBLIC_ROUTE_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));
  const isAuthRoute = AUTH_ROUTE_PATHS.has(location.pathname);
  const isWallRoute = WALL_ROUTE_PATHS.has(location.pathname);
  const isV2ShellRoute = V2_SHELL_ROUTE_PATHS.has(location.pathname);
  const shellVisible = !isPublicRoute && !isWallRoute;
  const sidebarVisible = shellVisible && !isV2ShellRoute;
  const mainWidth = sidebarVisible && open && !isMobile ? 'calc(100% - 205px)' : '100%';
  const contentClassName = isWallRoute ? 'wall-page' : isAuthRoute ? 'auth-page' : isPublicRoute ? 'public-page' : 'content';
  const [recentRoutes, setRecentRoutes] = useState(() => readRecentMenuRoutes());
  const recentItems = useMemo(() => getRecentMenuItems(recentRoutes), [recentRoutes]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const syncRecentRoutes = () => {
      setRecentRoutes(readRecentMenuRoutes());
    };

    window.addEventListener(RECENT_MENU_EVENT, syncRecentRoutes);
    window.addEventListener('storage', syncRecentRoutes);

    return () => {
      window.removeEventListener(RECENT_MENU_EVENT, syncRecentRoutes);
      window.removeEventListener('storage', syncRecentRoutes);
    };
  }, []);

  return (
    <div className={`app ${isPublicRoute ? 'app--public' : ''} ${isWallRoute ? 'app--wall' : ''} ${isV2ShellRoute ? 'app--v2' : ''}`}>
      {shellVisible && <Header open={open} setOpen={setOpen} isMobile={isMobile} />}

      <div style={{ display: 'flex' }}>
        {sidebarVisible && <Sidebar open={open} setOpen={setOpen} isMobile={isMobile} menuAccess={menuAccess} authProfile={authProfile} />}
        <Main open={open} shellVisible={shellVisible} isMobile={isMobile} style={{ width: mainWidth }}>
          {shellVisible && !isV2ShellRoute && recentItems.length > 0 && (
            <div className="app-shell-recent" aria-label="Recent pages">
              <div className="app-shell-recent__track">
                <span className="app-shell-recent__label">Recent</span>
                <div className="app-shell-recent__items">
                  {recentItems.map((item) => (
                    <NavLink
                      key={`recent-${item.to}`}
                      to={item.to}
                      title={`${item.label} • ${item.sectionTitle}`}
                      aria-label={`${item.label}, ${item.sectionTitle}`}
                      className={({ isActive }) =>
                        `app-shell-recent__item${isActive ? ' app-shell-recent__item--active' : ''}`
                      }
                    >
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>
          )}
          <div className={contentClassName}>
            <Suspense fallback={<RouteLoadingState shellVisible={shellVisible} />}>
              <AppRoutes token={token} saveToken={saveToken} />
            </Suspense>
          </div>
        </Main>
      </div>
      {!isWallRoute && <Footer />}
      {!isWallRoute && <VersionBadge />}
    </div>
  );
}

function RouteLoadingState({ shellVisible }) {
  return (
    <div
      style={{
        minHeight: shellVisible ? 'calc(100vh - 140px)' : '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#103d74',
        fontSize: '1rem',
        fontWeight: 600,
      }}
    >
      Loading page...
    </div>
  );
}

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [authProfile, setAuthProfile] = useState(() => readStoredAuthProfile());
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [open, setOpen] = useState(window.innerWidth > 900);
  const decodedToken = token ? jwtDecode(token) : {};

  const checkTokenValidity = (tokenToCheck) => {
    try {
      const decoded = jwtDecode(tokenToCheck);
      const currentTime = Date.now() / 1000;
      return decoded.exp >= currentTime;
    } catch (error) {
      console.error('Invalid token:', error.message);
      return false;
    }
  };

  const refreshAuthProfile = useCallback(async (tokenOverride = localStorage.getItem('token')) => {
    const activeToken = tokenOverride || localStorage.getItem('token');
    if (!activeToken) {
      localStorage.removeItem(AUTH_PROFILE_STORAGE_KEY);
      setAuthProfile(null);
      return null;
    }

    try {
      const profile = await getAuthProfile();
      localStorage.setItem(AUTH_PROFILE_STORAGE_KEY, JSON.stringify(profile));
      setAuthProfile(profile);
      return profile;
    } catch (error) {
      if (error?.response?.status !== 401) {
        console.error('Failed to refresh auth profile:', error);
      }
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!token) {
      localStorage.removeItem(AUTH_PROFILE_STORAGE_KEY);
      setAuthProfile(null);
      return undefined;
    }

    if (!checkTokenValidity(token)) {
      setAuthProfile(null);
      setToken(null);
      logoutAndRedirect({ message: 'Your session expired. Please sign in again.' });
      return undefined;
    }

    (async () => {
      const profile = await refreshAuthProfile(token);
      if (!cancelled && profile === null) {
        setAuthProfile((prev) => prev || readStoredAuthProfile());
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshAuthProfile, token]);

  useEffect(() => {
    const handleProfileRefresh = () => {
      if (localStorage.getItem('token')) {
        refreshAuthProfile();
      }
    };

    window.addEventListener('auth-profile-refresh', handleProfileRefresh);

    return () => {
      window.removeEventListener('auth-profile-refresh', handleProfileRefresh);
    };
  }, [refreshAuthProfile]);


  useEffect(() => {
    const handleAuthLogout = () => {
      setAuthProfile(null);
      setToken(null);
    };

    window.addEventListener('auth-logout', handleAuthLogout);

    return () => {
      window.removeEventListener('auth-logout', handleAuthLogout);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const nextIsMobile = window.innerWidth <= 900;
      setIsMobile((currentIsMobile) => {
        if (currentIsMobile !== nextIsMobile) {
          setOpen(!nextIsMobile);
        }
        return nextIsMobile;
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const currentMainBundleKey = getCurrentMainBundleKey();
    const storedGuard = sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY);

    if (storedGuard && storedGuard !== currentMainBundleKey) {
      sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY);
    }

    const attemptChunkRecovery = (errorLike) => {
      if (!isChunkLoadError(errorLike)) return;

      const activeGuard = sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY);
      if (activeGuard === currentMainBundleKey) {
        return;
      }

      sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, currentMainBundleKey);
      window.location.reload();
    };

    const handleWindowError = (event) => {
      attemptChunkRecovery(event?.error || event);
    };

    const handleUnhandledRejection = (event) => {
      attemptChunkRecovery(event?.reason || event);
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
  /*
    useEffect(() => {
      if (!token) {
        navigate('/login'); // Пренасочва към логин, ако няма токен
      }
    }, [token, navigate]);
  */
  const saveToken = (userToken) => {
    localStorage.setItem('token', userToken);
    localStorage.removeItem(AUTH_PROFILE_STORAGE_KEY);
    setAuthProfile(null);
    setToken(userToken);
  };
  const menuAccess = authProfile?.menuAccess || decodedToken.menuAccess || [];

  return (
    <Router>
      <AppLayout
        token={token}
        open={open}
        setOpen={setOpen}
        isMobile={isMobile}
        menuAccess={menuAccess}
        authProfile={authProfile}
        saveToken={saveToken}
      />
    </Router>
  );
}


export default App;

/*
      <Route
                path="/edit-playlist/:playlistId"
                element={
                  <ProtectedRoute token={token}>
                    <EditPlaylist />
                  </ProtectedRoute>
                }
              />
*/
